import React from "react";
import { Button, Typography } from "antd";
import { CopyOutlined, HomeOutlined, ReloadOutlined, LeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ReviewMarquee } from "./ReviewMarquee.js";
import { appFeedback } from "@/shared/feedback/appFeedback";

const MARQUEE_ENABLED =
  (import.meta as ImportMeta & { env: Record<string, string> }).env
    .VITE_ENABLE_MARQUEE === "true";

const { Text } = Typography;

const KEYFRAMES = `
@keyframes amax-orb-float {
  0%   { transform: translate(0, 0) scale(1); }
  33%  { transform: translate(30px, -40px) scale(1.08); }
  66%  { transform: translate(-25px, 25px) scale(0.94); }
  100% { transform: translate(0, 0) scale(1); }
}
@keyframes amax-err-rise {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes amax-err-glitch {
  0%, 92%, 100% { transform: translate(0, 0); filter: none; }
  93% { transform: translate(-2px, 1px); filter: hue-rotate(8deg); }
  95% { transform: translate(2px, -1px); }
  97% { transform: translate(-1px, 0); }
}
`;

type ErrorPageProps = {
  /** Big code shown in the hero, e.g. "404", "500", "403". */
  code?: string | number;
  /** Short bold headline. */
  title?: string;
  /** Supporting line under the title. */
  subtitle?: React.ReactNode;
  /** Optional technical detail (stack/message) shown in a collapsible block. */
  detail?: string;
  /** Show the "Thử lại" (retry) button + handler. */
  onRetry?: () => void;
  /** Override the primary action; defaults to "Về trang chủ". */
  primaryAction?: React.ReactNode;
};

/**
 * Full-screen, theme-aware error / not-found page.
 * Reused by both the 404 catch-all route and the runtime ErrorBoundary.
 */
export function ErrorPage({
  code = "404",
  title = "Không tìm thấy trang",
  subtitle = "Trang bạn tìm không tồn tại hoặc đã được di chuyển.",
  detail,
  onRetry,
  primaryAction,
}: ErrorPageProps) {
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = React.useState(false);

  const goHome = () => {
    // Hard navigate so a crashed boundary fully resets its subtree.
    if (window.location.pathname === "/") window.location.reload();
    else navigate("/", { replace: true });
  };

  const copyErrorDetails = async () => {
    const report = [
      `Mã lỗi: ${code}`,
      `Tiêu đề: ${title}`,
      `Trang: ${window.location.pathname}`,
      typeof subtitle === "string" ? `Thông báo: ${subtitle}` : undefined,
      detail ? `Chi tiết kỹ thuật:\n${detail}` : undefined,
    ]
      .filter(Boolean)
      .join("\n\n");

    const copied = await appFeedback.copyToClipboard(report);
    appFeedback.message(
      copied ? "success" : "warning",
      copied
        ? "Đã sao chép thông tin lỗi."
        : "Không thể sao chép tự động. Hãy kiểm tra quyền clipboard của trình duyệt.",
    );
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "var(--bg)",
      }}
    >
      <style>{KEYFRAMES}</style>

      {/* Ambient floating orbs */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "-12%",
          left: "-8%",
          width: 480,
          height: 480,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 30% 30%, var(--accent), transparent 70%)",
          opacity: 0.18,
          filter: "blur(8px)",
          animation: "amax-orb-float 18s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          bottom: "-15%",
          right: "-10%",
          width: 560,
          height: 560,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 60% 40%, var(--accent), transparent 70%)",
          opacity: 0.14,
          filter: "blur(8px)",
          animation: "amax-orb-float 22s ease-in-out infinite reverse",
          pointerEvents: "none",
        }}
      />

      {/* Subtle grid texture */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          opacity: 0.12,
          maskImage:
            "radial-gradient(circle at 50% 45%, black, transparent 72%)",
          WebkitMaskImage:
            "radial-gradient(circle at 50% 45%, black, transparent 72%)",
          pointerEvents: "none",
        }}
      />

      {/* Content region — scrolls independently so it never overlaps the marquee */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        {/* Content card */}
        <div
        style={{
          width: "100%",
          maxWidth: 480,
          textAlign: "center",
          animation: "amax-err-rise 0.5s ease-out both",
        }}
      >
        <div
          style={{
            fontSize: "clamp(96px, 22vw, 168px)",
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: "-0.05em",
            background:
              "linear-gradient(135deg, var(--accent), var(--text))",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: "amax-err-glitch 6s steps(1) infinite",
            userSelect: "none",
          }}
        >
          {code}
        </div>

        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: "var(--text)",
            marginTop: 8,
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </div>

        <Text
          style={{
            display: "block",
            color: "var(--text2)",
            fontSize: 14,
            marginTop: 10,
            lineHeight: 1.6,
          }}
        >
          {subtitle}
        </Text>

        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "center",
            marginTop: 28,
            flexWrap: "wrap",
          }}
        >
          {primaryAction ?? (
            <Button type="primary" icon={<HomeOutlined />} onClick={goHome}>
              Về trang chủ
            </Button>
          )}
          {onRetry ? (
            <Button icon={<ReloadOutlined />} onClick={onRetry}>
              Thử lại
            </Button>
          ) : (
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)}>
              Quay lại
            </Button>
          )}
          <Button icon={<CopyOutlined />} onClick={() => void copyErrorDetails()}>
            Sao chép thông tin lỗi
          </Button>
        </div>

        {detail && (
          <div style={{ marginTop: 28 }}>
            <Button
              type="link"
              size="small"
              onClick={() => setShowDetail((v) => !v)}
              style={{ color: "var(--text3)", fontSize: 12 }}
            >
              {showDetail ? "Ẩn chi tiết kỹ thuật" : "Xem chi tiết kỹ thuật"}
            </Button>
            {showDetail && (
              <pre
                style={{
                  textAlign: "left",
                  marginTop: 8,
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: "var(--bg2)",
                  border: "1px solid var(--border)",
                  color: "var(--text3)",
                  fontSize: 12,
                  lineHeight: 1.5,
                  overflow: "auto",
                  maxHeight: 220,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {detail}
              </pre>
            )}
          </div>
        )}
        </div>
      </div>

      {MARQUEE_ENABLED && <ReviewMarquee />}
    </div>
  );
}
